<script setup lang="ts">
import type { ServicesTotals } from '@/composables/jobEdit/useJobEditProductsServices';
import type { JobEditServiceItem } from '@/models/jobEdit';

import JobEditServiceFieldset from '@/components/fieldsets/JobEditServiceFieldset.vue';
import { createEmptyServiceItem } from '@/models/jobEdit';

export interface Props {
  readonly?: boolean;
  jobId?: number;
  jobDate?: string;
  legNumber?: number;
  stopTotal?: ServicesTotals;
  jobTotal?: ServicesTotals;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  jobId: undefined,
  jobDate: undefined,
  legNumber: undefined,
  stopTotal: () => ({
    quantity: 0,
    scheduledFee: 0,
    fee: 0,
    gst: 0,
    secondQuantity: 0,
    pay: 0,
  }),
  jobTotal: undefined,
});

const model = defineModel<JobEditServiceItem[]>({ default: () => [] });

function addService(): void {
  model.value = [...model.value, createEmptyServiceItem()];
}

function removeService(index: number): void {
  model.value = model.value.filter((_, i) => i !== index);
}

function handleServiceUpdate(index: number, value: JobEditServiceItem): void {
  const updated = [...model.value];
  updated[index] = value;
  model.value = updated;
}

defineExpose({
  addService,
});
</script>

<template>
  <fieldset
    :class="$style.jobEditServicesFieldset"
    data-testid="jobEditServicesFieldset"
  >
    <div :class="$style.header">
      <div :class="$style.headerCell">Service Code</div>
      <div :class="$style.headerCell">Quantity (fee)</div>
      <div :class="$style.headerCell">Sch. (fee)</div>
      <div :class="$style.headerCell">Fee</div>
      <div :class="$style.headerCell">GST</div>
      <div :class="$style.headerCell">Quantity (pay)</div>
      <div :class="$style.headerCell">Pay</div>
      <div :class="$style.headerCellAction"></div>
    </div>

    <div :class="$style.body">
      <JobEditServiceFieldset
        v-for="(service, index) in model"
        :key="index"
        :class="$style.row"
        :model-value="service"
        :readonly="props.readonly"
        :job-id="props.jobId"
        :job-date="props.jobDate"
        :leg-number="props.legNumber"
        @update:model-value="(value) => handleServiceUpdate(index, value)"
        @delete="removeService(index)"
      />
    </div>

    <div :class="$style.totalsSection">
      <div :class="$style.totalRow">
        <div :class="$style.totalLabel">Stop total</div>
        <div :class="$style.totalInputCell">{{ props.stopTotal.quantity }}</div>
        <div :class="$style.totalInputCell">
          ${{ props.stopTotal.scheduledFee.toFixed(2) }}
        </div>
        <div :class="$style.totalInputCell">
          ${{ props.stopTotal.fee.toFixed(2) }}
        </div>
        <div :class="$style.totalInputCell">
          ${{ props.stopTotal.gst.toFixed(2) }}
        </div>
        <div :class="$style.totalInputCell">
          {{ props.stopTotal.secondQuantity }}
        </div>
        <div :class="$style.totalInputCell">
          ${{ props.stopTotal.pay.toFixed(2) }}
        </div>
        <div :class="$style.totalCellAction"></div>
      </div>
      <div v-if="props.jobTotal !== undefined" :class="$style.totalRow">
        <div :class="$style.totalLabel">Job total</div>
        <div :class="$style.totalInputCell">{{ props.jobTotal.quantity }}</div>
        <div :class="$style.totalInputCell">
          ${{ props.jobTotal.scheduledFee.toFixed(2) }}
        </div>
        <div :class="$style.totalInputCell">
          ${{ props.jobTotal.fee.toFixed(2) }}
        </div>
        <div :class="$style.totalInputCell">
          ${{ props.jobTotal.gst.toFixed(2) }}
        </div>
        <div :class="$style.totalInputCell">
          {{ props.jobTotal.secondQuantity }}
        </div>
        <div :class="$style.totalInputCell">
          ${{ props.jobTotal.pay.toFixed(2) }}
        </div>
        <div :class="$style.totalCellAction"></div>
      </div>
    </div>
  </fieldset>
</template>

<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/grids';

.jobEditServicesFieldset {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: none;
  padding: 0;
  margin: 0;
}

.header {
  display: grid;
  grid-template-columns: grids.$servicesRowColumns;
  gap: 8px;
  padding: 8px 0 8px 8px;
  border-bottom: 2px solid colors.$color-gray-200;
}

.headerCell {
  font-size: 0.75rem;
  font-weight: 600;
  color: colors.$color-text-secondary;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.headerCellAction {
  width: 48px;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.row {
  background-color: colors.$color-gray-50;
  border-radius: 4px;
}

.totalsSection {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 2px solid colors.$color-gray-200;
  padding-top: 8px;
  margin-top: 4px;
}

.totalRow {
  display: grid;
  grid-template-columns: grids.$servicesRowColumns;
  gap: 8px;
  align-items: center;
}

.totalLabel {
  font-weight: 600;
  font-size: 0.8125rem;
  color: colors.$color-text-secondary;
  display: flex;
  align-items: center;
}

.totalInputCell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: 1px solid colors.$color-gray-200;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: colors.$color-white;
  color: colors.$color-text-primary;
  min-height: 36px;
}

.totalCellAction {
  width: 48px;
}
</style>
